import { FieldLabel } from "../shared/FieldLabel";
import { recordSpendingFocus } from "../project/spendingNavigation";
import type { ProjectDestination } from "../project/projectNavigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { userMessage } from "../shared/userMessage";
import { track } from "../../telemetry/telemetry";
import { useAppDb } from "../../AppContext";
import type { ProjectSummary } from "../../domain/project";
import type { ProjectStatus } from "../../domain/status";
import { STATUS_IDS } from "../../domain/status";
import { formatCents } from "../../domain/money";
import { ValidationError } from "../../db/repositories/projectRepository";
import { ProjectForm, emptyProjectInput } from "./ProjectForm";
import type { RegisterBeforeLeave } from "../shared/useManualDraftGuard";
import { listSharedWithMe, type SharedProjectCard } from "../../db/sharedClient";
import { deriveActivation, type Activation } from "../../engine/activation";
import type { ProjectStrategy } from "../../domain/project";
import {
  ArrowRight,
  Building2,
  Calculator,
  Compass,
  CheckCircle2,
  DatabaseBackup,
  Hammer,
  Home,
  LayoutGrid,
  List,
  MapPin,
  PlayCircle,
  Plus,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import { Badge, Callout, EmptyState, Meter, Metric, PageHeader, Segmented } from "../shared/ui";
import { compactMoney } from "../shared/charts";

interface ProjectsScreenProps {
  registerBeforeLeave?: RegisterBeforeLeave;
  onOpenProject: (projectId: string, destination?: ProjectDestination, focusId?: string) => void;
  /** Open a project somebody else owns and has shared (spec §65E). */
  onOpenShared?: (projectId: string) => void;
  /** Spec §73: the business-level capital plan (owner's workspace only). */
  /** Spec §88A: the first-use welcome offers the three ways in. */
  firstName?: string;
  onOpenDeals?: () => void;
  onOpenFind?: () => void;
  /** Spec §90: the workspace's reusable renovation standards. */
  onOpenStandards?: () => void;
  /** Straight after onboarding: the welcome says so. */
  justOnboarded?: boolean;
  /** Spec §86: what the person said they mostly do, as a starting default only. */
  defaultStrategy?: ProjectStrategy;
}

/**
 * Landing workspace: list of saved projects with search/status filter,
 * plus the New Project workflow.
 */
export function ProjectsScreen({ onOpenProject, onOpenShared, defaultStrategy, firstName, onOpenDeals, onOpenFind, onOpenStandards, justOnboarded, registerBeforeLeave }: ProjectsScreenProps) {
  const db = useAppDb();
  const { projects: projectRepo, statuses: statusRepo, backup, activation: activationRepo } = db;
  /** Spec §90: the standard a new project starts from (the default, unless changed). */
  const [standards, setStandards] = useState<Array<{ id: string; name: string; isDefault: boolean }>>([]);
  const [standardId, setStandardId] = useState<string | null>(null);
  /* Spec §86-7: where every project stands, derived in one bounded read, so
   * a returning user sees which project to pick up and what it needs. */
  const [progress, setProgress] = useState<Map<string, Activation>>(new Map());
  const [estimates, setEstimates] = useState<Map<string, number>>(new Map());
  /* Cards or a dense list: a per-device preference, nothing more. */
  const [layout, setLayout] = useState<"cards" | "list">(() => {
    try {
      return window.localStorage.getItem("berrier.projects.layout") === "list" ? "list" : "cards";
    } catch {
      return "cards";
    }
  });
  const chooseLayout = (next: "cards" | "list") => {
    setLayout(next);
    try {
      window.localStorage.setItem("berrier.projects.layout", next);
    } catch {
      /* storage unavailable — the choice lasts for this visit */
    }
  };
  const [backupState, setBackupState] = useState<
    { kind: "idle" } | { kind: "running" } | { kind: "done"; path: string; count: number; kb: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [statuses, setStatuses] = useState<ProjectStatus[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingStandard, setPendingStandard] = useState<{projectId:string;standardId:string;name:string} | null>(null);

  /**
   * SHARED WITH ME (spec §65E) — projects somebody else owns and has given
   * this person access to. They are kept deliberately apart from My
   * Projects: they are not this person's projects, they keep the owner's
   * job number, and nothing about them belongs in this workspace's list.
   */
  const [shared, setShared] = useState<SharedProjectCard[]>([]);

  const [search, setSearch] = useState("");
  const [quickProject, setQuickProject] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const reload = useCallback(async () => {
    try {
      const [projectRows, statusRows] = await Promise.all([
        projectRepo.listProjects(),
        statusRepo.listStatuses(),
      ]);
      setProjects(projectRows);
      setStatuses(statusRows);
      setLoadError(null);
      try {
        const snapshots = await activationRepo.snapshots();
        setProgress(new Map(snapshots.map((s) => [s.projectId, deriveActivation(s)])));
        setEstimates(new Map(snapshots.map((s) => [s.projectId, s.estimateCents])));
      } catch {
        /* progress is a convenience; the list never waits on it */
      }
    } catch (err) {
      console.error("Failed to load projects:", err);
      setLoadError("Could not load projects from the database.");
    }
  }, [projectRepo, statusRepo, activationRepo]);

  useEffect(() => {
    void reload();
  }, [reload]);


  useEffect(() => {
    // Sharing exists only on the hosted deployment. Everywhere else this
    // simply finds nothing, and the section stays out of the way.
    let alive = true;
    listSharedWithMe()
      .then((cards) => {
        if (alive) setShared(cards);
      })
      .catch(() => {
        if (alive) setShared([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const visibleProjects = useMemo(() => {
    if (!projects) return [];
    const needle = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== "all" && p.statusId !== statusFilter) return false;
      if (needle === "") return true;
      const haystack = `${p.jobNumber} ${p.name} ${p.streetAddress} ${p.city} ${p.state} ${p.zip}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [projects, search, statusFilter]);

  useEffect(() => {
    let alive = true;
    void db.standards
      .list()
      .then((list) => {
        if (!alive) return;
        setStandards(list.map((s) => ({ id: s.id, name: s.name, isDefault: s.isDefault })));
        setStandardId((current) => current ?? list.find((s) => s.isDefault)?.id ?? null);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [db]);

  async function handleCreate(input: Parameters<typeof projectRepo.createProject>[0]) {
    setSaving(true);
    setSaveError(null);
    try {
      const created = await projectRepo.createProject(input);
      track("project.created", { projectType: input.projectType ?? "renovation" }, "projects");
      /*
       * Spec §90-7: the project takes its own COPY of the standard — its
       * finishes become the project's own §82 defaults and the snapshot
       * records what the standard said — so tomorrow's edit to the standard
       * cannot reach back into this project.
       */
      if (standardId) {
        try {
          await db.standards.applyToProject(created.id, standardId);
        } catch (err) {
          console.error("The renovation standard could not be applied:", err);
          setPendingStandard({projectId:created.id,standardId,name:standards.find(s=>s.id===standardId)?.name??"Selected standard"});
          setCreating(false);
          await reload();
          return;
        }
      }
      setCreating(false);
      await reload();
      onOpenProject(created.id);
    } catch (err) {
      console.error("Failed to create project:", err);
      setSaveError(
        err instanceof ValidationError
          ? err.message
          : "The project could not be saved to the database.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (pendingStandard) {
    return <section className="panel" aria-labelledby="standard-failed-title">
      <h2 id="standard-failed-title">Project saved · standard needs attention</h2>
      <div className="error-panel" role="alert"><p>Your project was created, but the renovation standard was not applied. Its finishes and prices must not be assumed.</p><p>{pendingStandard.name}</p>{saveError&&<p>{saveError}</p>}</div>
      <p className="muted">Retry uses this same project; it does not create another job. You can also explicitly continue without the standard.</p>
      <div className="form-actions"><button className="btn btn-primary" disabled={saving} onClick={async()=>{
        setSaving(true);setSaveError(null);
        try{await db.standards.applyToProject(pendingStandard.projectId,pendingStandard.standardId);const id=pendingStandard.projectId;setPendingStandard(null);onOpenProject(id);}
        catch(err){setSaveError(userMessage(err,"The standard could not be applied. Your saved project remains available."));}
        finally{setSaving(false);}
      }}>Retry standard</button><button className="btn" disabled={saving} onClick={()=>{const id=pendingStandard.projectId;setPendingStandard(null);onOpenProject(id);}}>Continue without standard</button></div>
    </section>;
  }

  if (creating) {
    return (
      <ProjectForm
        registerBeforeLeave={registerBeforeLeave}
        title="New Project"
        statuses={statuses}
        standards={standards}
        standardId={standardId}
        onStandardChange={setStandardId}
        initial={emptyProjectInput(STATUS_IDS.planning, defaultStrategy)}
        submitLabel="Create project"
        mode="create"
        busy={saving}
        externalError={saveError}
        onSubmit={(input) => void handleCreate(input)}
        onCancel={() => {
          setCreating(false);
          setSaveError(null);
        }}
      />
    );
  }

  const live = (projects ?? []).filter((p) => p.statusId !== "archived");
  const portfolio = {
    active: live.length,
    purchase: live.reduce((sum, p) => sum + (p.purchasePriceCents ?? 0), 0),
    estimates: live.reduce((sum, p) => sum + (estimates.get(p.id) ?? 0), 0),
    arv: live.reduce((sum, p) => sum + (p.expectedArvCents ?? 0), 0),
    ready: live.filter((p) => progress.get(p.id)?.firstEstimateReached).length,
  };

  return (
    <div className="projects-home">
      <PageHeader
        title="Projects"
        lede={projects !== null && projects.length === 0 ? undefined : "Every property you are renovating or building, with where each one stands."}
        actions={
          <>
            {backup && (
              <button
                className="btn btn-ghost"
                disabled={backupState.kind === "running"}
                title="Creates a consistent database snapshot + all attachments as one zip in the backups folder."
                onClick={() => {
                  setBackupState({ kind: "running" });
                  void backup
                    .backupAll()
                    .then((r) =>
                      setBackupState({
                        kind: "done",
                        path: r.absolutePath,
                        count: r.attachmentCount,
                        kb: Math.round(r.sizeBytes / 1024),
                      }),
                    )
                    .catch((err: unknown) => {
                      console.error("Backup failed:", err);
                      setBackupState({
                        kind: "error",
                        message: userMessage(err, "Backup failed."),
                      });
                    });
                }}
              >
                <DatabaseBackup size={16} aria-hidden="true" />
                {backupState.kind === "running" ? "Backing up…" : "Back up all data"}
              </button>
            )}
            {onOpenStandards && (
              <button className="btn" onClick={onOpenStandards} title="The finishes, packages and prices you reuse on every house">
                Renovation standards
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus size={16} aria-hidden="true" /> New project
            </button>
          </>
        }
      />
      {backupState.kind === "done" && (
        <Callout tone="good">
          Backup complete: <code>{backupState.path}</code> ({backupState.kb} KB, {backupState.count} attachment{backupState.count === 1 ? "" : "s"}). See
          BACKUP-README.txt inside for restore steps.
        </Callout>
      )}
      {backupState.kind === "error" && <Callout tone="bad">Backup NOT created: {backupState.message}</Callout>}
      {loadError && <Callout tone="bad">{loadError}</Callout>}

      {(() => {
        /* CONTINUE WHERE YOU LEFT OFF (spec §86-7): the most recently touched
         * project that is still short of its first estimate. Nothing about
         * this is remembered — it is whatever the data says today. */
        const resume = (projects ?? []).find((p) => {
          const a = progress.get(p.id);
          return a !== undefined && !a.firstEstimateReached && a.next !== null && p.statusId !== "archived";
        });
        const a = resume ? progress.get(resume.id) : undefined;
        if (!resume || !a || !a.next) return null;
        return (
          <div className="panel resume-card">
            <span className="resume-icon" aria-hidden="true">
              <PlayCircle size={22} />
            </span>
            <div>
              <span className="page-eyebrow">Continue where you left off</span>
              <h2>{resume.name}</h2>
              <p>
                <strong>{a.next.title}.</strong> {a.next.detail}
              </p>
            </div>
            <button className="btn btn-primary" onClick={() => onOpenProject(resume.id, a.next?.action.tab)}>
              Continue <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
        );
      })()}

      {projects && projects.length > 0 && (
        <section className="panel operator-shortcuts" aria-label="Go directly to work">
          <FieldLabel htmlFor="quick-project"><strong>Go directly to work</strong></FieldLabel>
          <select id="quick-project" value={quickProject || projects[0].id} onChange={(event) => setQuickProject(event.target.value)}>
            {projects.map((item) => <option key={item.id} value={item.id}>{item.jobNumber ? item.jobNumber + " · " : ""}{item.name}</option>)}
          </select>
          <div className="toolbar">
            <button className="btn btn-small" onClick={() => onOpenProject(quickProject || projects[0].id, "scope")}>Review estimate</button>
            <button className="btn btn-small" onClick={() => onOpenProject(quickProject || projects[0].id, "bids")}>Compare quotes</button>
            <button className="btn btn-small" onClick={() => onOpenProject(quickProject || projects[0].id, "accounting", recordSpendingFocus())}>Record spending</button>
            <button className="btn btn-small" onClick={() => onOpenProject(quickProject || projects[0].id, "reconciliation")}>Review variance</button>
          </div>
        </section>
      )}

      {projects !== null && projects.length > 0 && (
        <div className="metric-row projects-metrics">
          <Metric label="Active projects" icon={Building2} value={portfolio.active} sub={`${portfolio.ready} with priced scope`} />
          <Metric label="Purchase prices" icon={Home} value={compactOrDash(portfolio.purchase)} sub="As entered on each project" />
          <Metric label="Calculated estimates" icon={Hammer} value={compactOrDash(portfolio.estimates)} sub="Renovation scope priced so far" />
          <Metric label="Expected ARV" icon={TrendingUp} value={compactOrDash(portfolio.arv)} sub="As entered on each project" />
        </div>
      )}

      {projects !== null && projects.length > 0 && (
        <div className="list-toolbar">
          <span className="search-field">
            <Search size={16} aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by job, name or address" aria-label="Search projects" />
          </span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
            <option value="all">All statuses</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName}
              </option>
            ))}
          </select>
          <span className="list-toolbar-end">
            <Segmented
              label="Layout"
              value={layout}
              onChange={chooseLayout}
              options={[
                { id: "cards", label: <LayoutGrid size={16} aria-label="Cards" /> },
                { id: "list", label: <List size={16} aria-label="List" /> },
              ]}
            />
          </span>
        </div>
      )}

      {projects === null ? (
        <div className="project-grid" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="panel project-card">
              <span className="skeleton" style={{ height: 14, width: "40%" }} />
              <span className="skeleton" style={{ height: 22, width: "70%", marginTop: 10 }} />
              <span className="skeleton" style={{ height: 60, marginTop: 16 }} />
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <WelcomeStart
          firstName={firstName}
          justOnboarded={justOnboarded}
          onCreateProject={() => setCreating(true)}
          onOpenDeals={onOpenDeals}
          onOpenFind={onOpenFind}
        />
      ) : visibleProjects.length === 0 ? (
        <EmptyState icon={Search} title="No projects match">
          Try a different search or status.
        </EmptyState>
      ) : layout === "list" ? (
        <div className="panel panel-flush">
          <table className="data-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Project</th>
                <th>Status</th>
                <th>Where it stands</th>
                <th className="num">Purchase</th>
                <th className="num">Working estimate</th>
                <th className="num">Expected ARV</th>
              </tr>
            </thead>
            <tbody>
              {visibleProjects.map((p) => {
                const a = progress.get(p.id);
                return (
                  <tr key={p.id} onClick={() => onOpenProject(p.id)} className="row-link">
                    <td className="cell-strong">{p.jobNumber}</td>
                    <td>
                      <button className="link-button cell-strong" onClick={(e) => { e.stopPropagation(); onOpenProject(p.id); }}>
                        {p.name}
                      </button>
                      <span className="cell-note-muted">{addressLine(p)}</span>
                    </td>
                    <td>
                      <span className={`status-badge status-${p.statusId}`}>{p.statusDisplayName}</span>
                    </td>
                    <td className="muted">{a ? (a.firstEstimateReached ? "Budget ready" : a.next ? `Next: ${a.next.title.toLowerCase()}` : "") : ""}</td>
                    <td className="num">{formatCents(p.purchasePriceCents)}</td>
                    <td className="num">{(estimates.get(p.id) ?? 0) > 0 ? formatCents(estimates.get(p.id)!) : "—"}</td>
                    <td className="num">{formatCents(p.expectedArvCents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="project-grid">
          {visibleProjects.map((p) => {
            const a = progress.get(p.id);
            return (
              <li key={p.id}>
                <button className="panel project-card" onClick={() => onOpenProject(p.id)}>
                  <span className="project-card-top">
                    <span className="job-chip">{p.jobNumber}</span>
                    <span className={`status-badge status-${p.statusId}`}>{p.statusDisplayName}</span>
                    {p.projectType === "new_construction" && <Badge>New construction</Badge>}
                  </span>
                  <span className="project-card-name">{p.name}</span>
                  <span className="project-card-address">
                    <MapPin size={13} aria-hidden="true" /> {addressLine(p) || "No address yet"}
                  </span>
                  <span className="project-card-figures">
                    <span>
                      <span className="figure-label">Purchase</span>
                      <span className="figure-value">{formatWhole(p.purchasePriceCents)}</span>
                    </span>
                    <span>
                      <span className="figure-label">Estimate</span>
                      <span className="figure-value">{(estimates.get(p.id) ?? 0) > 0 ? formatWhole(estimates.get(p.id)!) : "—"}</span>
                    </span>
                    <span>
                      <span className="figure-label">Expected ARV</span>
                      <span className="figure-value">{formatWhole(p.expectedArvCents)}</span>
                    </span>
                  </span>
                  {a && (
                    <span className="project-card-progress">
                      <span className="project-card-progress-text">
                        <span>
                          {a.firstEstimateReached ? (
                            <>
                              <CheckCircle2 size={14} aria-hidden="true" className="icon-good" /> Budget ready
                            </>
                          ) : a.next ? (
                            <>Next: {a.next.title.toLowerCase()}</>
                          ) : (
                            "Getting started"
                          )}
                        </span>
                        <span className="muted">
                          {a.setupDone} of {a.setupTotal} setup steps
                        </span>
                      </span>
                      <Meter fraction={a.setupTotal ? a.setupDone / a.setupTotal : 0} label={`${p.name}: ${a.setupDone} of ${a.setupTotal} setup steps done`} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {shared.length > 0 && (
        <section className="shared-with-me">
          <h2>Shared with me</h2>
          <p className="muted">Projects other people own and have shared with you. They stay in their owner&rsquo;s records — you can work on the plans you create for them.</p>
          <ul className="project-grid">
            {shared.map((card) => (
              <li key={card.projectId}>
                <button className="panel project-card project-card-shared" onClick={() => onOpenShared?.(card.projectId)}>
                  <span className="project-card-top">
                    <span className="job-chip">{card.jobNumber ?? "—"}</span>
                    <Badge tone="brand">
                      <Users size={12} aria-hidden="true" /> {card.ownerName}
                    </Badge>
                  </span>
                  <span className="project-card-name">{card.name}</span>
                  <span className="project-card-address">
                    <MapPin size={13} aria-hidden="true" /> {[card.street, card.city, card.state].filter(Boolean).join(", ") || "No address"}
                  </span>
                  <span className="muted small">{card.role === "design-collaborator" ? "You can work on designs" : card.role}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function addressLine(p: { streetAddress: string; city: string; state: string; zip: string }): string {
  return `${[p.streetAddress, p.city, p.state].filter(Boolean).join(", ")}${p.zip ? ` ${p.zip}` : ""}`;
}

/** Whole dollars on cards: a project list is for recognising, not reconciling. */
function formatWhole(cents: number | null): string {
  return cents === null ? "—" : `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}

function compactOrDash(cents: number): string {
  return cents === 0 ? "—" : compactMoney(cents);
}

/**
 * THE FIRST THING A NEW ACCOUNT SEES (spec §88A-8/9). One screen, three
 * honest ways in — a project, a deal, or the sample market in Find — instead
 * of a welcome banner and an empty state both saying "create a project".
 */
function WelcomeStart({
  firstName,
  justOnboarded,
  onCreateProject,
  onOpenDeals,
  onOpenFind,
}: {
  firstName?: string;
  justOnboarded?: boolean;
  onCreateProject: () => void;
  onOpenDeals?: () => void;
  onOpenFind?: () => void;
}) {
  const paths = [
    {
      key: "project",
      icon: Building2,
      title: "Start a renovation project",
      text: "Add the address and the rooms. Berrier Reno prices the work, builds the bid package and tracks the money.",
      action: "Create a project",
      onClick: onCreateProject,
      primary: true,
    },
    ...(onOpenDeals
      ? [
          {
            key: "deal",
            icon: Calculator,
            title: "Analyze a deal",
            text: "Underwrite a house you are considering — purchase, rehab, financing, flip or rental — before anything is a project.",
            action: "Analyze a deal",
            onClick: onOpenDeals,
            primary: false,
          },
        ]
      : []),
    ...(onOpenFind
      ? [
          {
            key: "find",
            icon: Compass,
            title: "Explore Find",
            text: "See how Find screens listings and reads nearby sales, using a fictional sample market.",
            action: "Open Find",
            onClick: onOpenFind,
            primary: false,
          },
        ]
      : []),
  ];
  return (
    <section className="welcome-start" aria-labelledby="welcome-start-title">
      <div className="welcome-start-head">
        {justOnboarded && (
          <Badge tone="good" dot>
            You are set up
          </Badge>
        )}
        <h2 id="welcome-start-title">{firstName ? `Welcome, ${firstName}. Where would you like to start?` : "Where would you like to start?"}</h2>
        <p className="muted">Pick one — everything else is a click away in the top bar, and nothing you create is visible to anyone else.</p>
      </div>
      <ul className="welcome-paths">
        {paths.map(({ key, icon: Icon, title, text, action, onClick, primary }) => (
          <li key={key}>
            <button className={`welcome-path ${primary ? "welcome-path-primary" : ""}`} onClick={onClick}>
              <span className="welcome-path-icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <span className="welcome-path-title">{title}</span>
              <span className="welcome-path-text">{text}</span>
              <span className="welcome-path-action">
                {action} <ArrowRight size={16} aria-hidden="true" />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
